import { del, download, get, getBlob, patch, post, put } from './client'
import type * as T from './types'

/**
 * One typed function per endpoint, grouped by area. Screens call these; nothing
 * in a view builds a URL by hand.
 */

export const customersApi = {
  search: (params: {
    page?: number; size?: number; search?: string; segmentId?: number
    status?: T.CustomerStatus; sort?: string
  }) => get<T.Page<T.Customer>>('/customers', { params }),

  byId: (id: number) => get<T.Customer>(`/customers/${id}`),

  byPhone: (phone: string) => get<T.Customer>('/customers/lookup', { params: { phone } }),

  create: (body: T.CustomerWrite) => post<T.Customer>('/customers', body),

  update: (id: number, body: T.CustomerWrite) => patch<T.Customer>(`/customers/${id}`, body),

  assignSegment: (id: number, segmentId: number, reason?: string) =>
    put<T.Customer>(`/customers/${id}/segment`, { segmentId, reason }),

  changeStatus: (id: number, status: T.CustomerStatus, reason?: string) =>
    put<T.Customer>(`/customers/${id}/status`, { status, reason }),

  gmv: (id: number, months = 12) =>
    get<T.CustomerGmv>(`/customers/${id}/gmv`, { params: { months } }),

  membership: (id: number) => get<T.CustomerMembership>(`/customers/${id}/membership`),

  /** Returns the customer to the segment they were in before approval. */
  revokeMembership: (id: number, reason: string) =>
    post<unknown>(`/customers/${id}/membership/revoke`, { reason }),
}

export const segmentsApi = {
  list: (activeOnly = false) => get<T.Segment[]>('/segments', { params: { activeOnly } }),
  byId: (id: number) => get<T.Segment>(`/segments/${id}`),
  create: (body: T.SegmentWrite) => post<T.Segment>('/segments', body),
  update: (id: number, body: T.SegmentWrite) => patch<T.Segment>(`/segments/${id}`, body),
  setActive: (id: number, active: boolean) =>
    put<T.Segment>(`/segments/${id}/activation`, { active }),
}

export const segmentApplicationsApi = {
  search: (params: {
    status?: T.SegmentApplicationStatus; segmentId?: number; page?: number; size?: number
  }) => get<T.Page<T.SegmentApplication>>('/segment-applications', { params }),

  pendingCount: () => get<T.PendingApplicationCount>('/segment-applications/pending-count'),

  byId: (id: number) => get<T.SegmentApplicationDetail>(`/segment-applications/${id}`),

  /** The decrypted card photo. Every view is logged by the service. */
  card: (id: number, side: T.CardSide) => getBlob(`/segment-applications/${id}/card/${side}`),

  approve: (id: number) => post<T.SegmentApplication>(`/segment-applications/${id}/approve`),

  reject: (id: number, reason: string) =>
    post<T.SegmentApplication>(`/segment-applications/${id}/reject`, { reason }),
}

export const programApi = {
  get: () => get<T.Program>('/program'),
  update: (body: Partial<T.Program>) => put<T.Program>('/program', body),
  channelRules: () => get<T.ChannelRule[]>('/program/channel-rules'),
  createChannelRule: (body: Partial<T.ChannelRule>) =>
    post<T.ChannelRule>('/program/channel-rules', body),
  updateChannelRule: (id: number, body: Partial<T.ChannelRule>) =>
    put<T.ChannelRule>(`/program/channel-rules/${id}`, body),
  deleteChannelRule: (id: number) => del(`/program/channel-rules/${id}`),
}

export const pointsApi = {
  account: (customerId: number) => get<T.PointsAccount>(`/customers/${customerId}/points`),

  earn: (customerId: number, body: T.EarnRequest) =>
    post<T.PointsOperationResult>(`/customers/${customerId}/points/earn`, body),

  adjust: (customerId: number, body: T.AdjustRequest) =>
    post<T.PointsOperationResult>(`/customers/${customerId}/points/adjust`, body),

  buckets: (customerId: number) => get<T.PointBucket[]>(`/customers/${customerId}/points/buckets`),

  expiring: (params: { withinDays?: number; page?: number; size?: number }) =>
    get<T.ExpiringPointsPage>('/points/expiring', { params }),
}

export const transactionsApi = {
  search: (params: {
    customerId?: number; type?: T.TransactionType; channel?: string
    orderReference?: string; from?: string; to?: string; page?: number; size?: number
  }) => get<T.Page<T.Transaction>>('/transactions', { params }),

  exportCsv: (params: { customerId?: number; type?: T.TransactionType; from?: string; to?: string }) =>
    download('/transactions/export', { params, fallbackName: 'loyalty-ledger.csv' }),
}

export const redemptionsApi = {
  quote: (body: T.RedemptionQuoteRequest) => post<T.RedemptionQuote>('/redemptions/quote', body),

  initiate: (body: T.RedemptionInitiateRequest) => post<T.Redemption>('/redemptions', body),

  confirm: (reference: string, otp: string) =>
    post<T.RedemptionResult>(`/redemptions/${reference}/confirm`, { otp }),

  resendOtp: (reference: string) => post<T.Redemption>(`/redemptions/${reference}/resend-otp`),

  cancel: (reference: string, reason?: string) =>
    post<T.Redemption>(`/redemptions/${reference}/cancel`, { reason }),

  byReference: (reference: string) => get<T.Redemption>(`/redemptions/${reference}`),

  search: (params: {
    customerId?: number; status?: T.RedemptionStatus; from?: string; to?: string
    page?: number; size?: number
  }) => get<T.Page<T.Redemption>>('/redemptions', { params }),
}

export const refundsApi = {
  search: (params: {
    status?: T.RefundStatus; customerId?: number; requestedBy?: number
    from?: string; to?: string; page?: number; size?: number
  }) => get<T.Page<T.Refund>>('/refunds', { params }),

  byReference: (reference: string) => get<T.Refund>(`/refunds/${reference}`),

  request: (body: { transactionId: number; reason: string; orderReference?: string }) =>
    post<T.Refund>('/refunds', body),

  approve: (reference: string, note?: string) =>
    post<T.Refund>(`/refunds/${reference}/approve`, { note }),

  reject: (reference: string, note: string) =>
    post<T.Refund>(`/refunds/${reference}/reject`, { note }),

  cancel: (reference: string) => post<T.Refund>(`/refunds/${reference}/cancel`),

  pendingCount: () => get<T.PendingRefundCount>('/refunds/pending-count'),

  reversibleTransactions: (customerId: number, params?: { page?: number; size?: number }) =>
    get<T.Page<T.ReversibleTransaction>>('/refunds/reversible-transactions', {
      params: { customerId, ...params },
    }),
}

export const counterApi = {
  /** 409 COUNTER_STAFF_UNASSIGNED when the signed-in user has no branch. */
  me: () => get<T.CounterAssignment>('/counter/me'),

  lookup: (body: T.CounterLookupRequest) => post<T.CounterCustomer>('/counter/lookup', body),

  quote: (body: T.CounterQuoteRequest) => post<T.CounterQuote>('/counter/quote', body),

  record: (body: T.CounterEntryRequest) => post<T.CounterEntry>('/counter/entries', body),

  /** Staff see their own branch; `branchId` is honoured only with COUNTER_REPORT. */
  entries: (params: { date?: string; branchId?: number; page?: number; size?: number }) =>
    get<T.Page<T.CounterEntry>>('/counter/entries', { params }),

  entry: (reference: string) =>
    get<T.CounterEntry>(`/counter/entries/${encodeURIComponent(reference)}`),

  confirmOtp: (reference: string, otp: string) =>
    post<T.CounterEntry>(`/counter/entries/${encodeURIComponent(reference)}/confirm-otp`, { otp }),

  resendOtp: (reference: string) =>
    post<T.CounterEntry>(`/counter/entries/${encodeURIComponent(reference)}/resend-otp`),

  cancel: (reference: string) =>
    post<T.CounterEntry>(`/counter/entries/${encodeURIComponent(reference)}/cancel`),

  requestReversal: (reference: string, reason: string) =>
    post<T.CounterEntry>(`/counter/entries/${encodeURIComponent(reference)}/reversal-request`, { reason }),

  branches: () => get<T.CounterBranch[]>('/counter/branches'),
  createBranch: (body: T.CounterBranchWrite) => post<T.CounterBranch>('/counter/branches', body),
  updateBranch: (id: number, body: T.CounterBranchWrite) =>
    put<T.CounterBranch>(`/counter/branches/${id}`, body),

  settings: () => get<T.CounterSettings>('/counter/settings'),
  updateSettings: (body: T.CounterSettings) => put<T.CounterSettings>('/counter/settings', body),

  staff: () => get<T.CounterStaff[]>('/counter/staff'),
  /** `null` unassigns. */
  assignStaff: (userId: number, branchId: number | null) =>
    put<T.CounterStaff>(`/counter/staff/${userId}`, { branchId }),

  report: (params: { date: string; branchId?: number }) =>
    get<T.CounterReport>('/counter/report', { params }),
}

export const reportsApi = {
  catalogue: (category?: T.ReportCategory) =>
    get<T.ReportDefinition[]>('/reports', { params: category ? { category } : {} }),

  definition: (code: string) => get<T.ReportDefinition>(`/reports/${code}`),

  run: (code: string, parameters: Record<string, string>, page = 0, size = 50) =>
    post<T.ReportResult>(`/reports/${code}/run`, { parameters }, { params: { page, size } }),

  export: (code: string, parameters: Record<string, string>, format: T.ReportFormat) =>
    download(`/reports/${code}/export`, {
      method: 'POST',
      body: { parameters },
      params: { format },
      fallbackName: `${code.toLowerCase().replace(/_/g, '-')}.${format.toLowerCase()}`,
    }),
}

export const bulkApi = {
  list: (params: { type?: T.BulkImportType; status?: T.BulkImportStatus; page?: number; size?: number }) =>
    get<T.Page<T.BulkImport>>('/bulk-imports', { params }),

  byReference: (reference: string) => get<T.BulkImport>(`/bulk-imports/${reference}`),

  errors: (reference: string) => get<T.BulkImportRowError[]>(`/bulk-imports/${reference}/errors`),

  upload: (type: T.BulkImportType, file: File, dryRun: boolean) => {
    const form = new FormData()
    form.append('file', file)
    return post<T.BulkImport>(`/bulk-imports/${endpointFor(type)}`, form, {
      params: { dryRun },
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  template: (type: T.BulkImportType) =>
    download(`/bulk-imports/templates/${type}`, {
      fallbackName: `${type.toLowerCase().replace(/_/g, '-')}-template.xlsx`,
    }),

  exportErrors: (reference: string) =>
    download(`/bulk-imports/${reference}/errors/export`, {
      fallbackName: `${reference}-errors.csv`,
    }),
}

function endpointFor(type: T.BulkImportType): string {
  switch (type) {
    case 'CUSTOMERS': return 'customers'
    case 'BALANCES': return 'balances'
    case 'CUSTOMER_SEGMENTS': return 'customer-segments'
  }
}

export const smsApi = {
  providers: () => get<T.SmsProvider[]>('/sms/providers'),
  provider: (id: number) => get<T.SmsProvider>(`/sms/providers/${id}`),
  createProvider: (body: T.SmsProviderWrite) => post<T.SmsProvider>('/sms/providers', body),
  updateProvider: (id: number, body: T.SmsProviderWrite) =>
    put<T.SmsProvider>(`/sms/providers/${id}`, body),
  deleteProvider: (id: number) => del(`/sms/providers/${id}`),
  activateProvider: (id: number) => post<T.SmsProvider>(`/sms/providers/${id}/activate`),
  testProvider: (id: number, phone: string, message?: string) =>
    post<T.SmsTestResult>(`/sms/providers/${id}/test`, { phone, message }),

  messages: (params: {
    status?: T.SmsMessageStatus; phone?: string; category?: string
    from?: string; to?: string; page?: number; size?: number
  }) => get<T.Page<T.SmsMessage>>('/sms/messages', { params }),

  retryMessage: (id: number) => post<T.SmsMessage>(`/sms/messages/${id}/retry`),
}

export const notificationsApi = {
  list: () => get<T.NotificationSetting[]>('/notifications/settings'),
  get: (eventType: T.NotificationEventType) =>
    get<T.NotificationSetting>(`/notifications/settings/${eventType}`),
  update: (eventType: T.NotificationEventType,
           body: { enabled: boolean; template: string; language?: string }) =>
    put<T.NotificationSetting>(`/notifications/settings/${eventType}`, body),
  preview: (eventType: T.NotificationEventType, template?: string, values?: Record<string, string>) =>
    post<T.NotificationPreview>(`/notifications/settings/${eventType}/preview`, { template, values }),
}

export const iamApi = {
  me: () => get<T.CurrentUser>('/iam/me'),

  users: (params: { search?: string; status?: T.UserStatus; page?: number; size?: number }) =>
    get<T.Page<T.User>>('/iam/users', { params }),

  user: (id: number) => get<T.User>(`/iam/users/${id}`),
  createUser: (body: T.UserWrite) => post<T.User>('/iam/users', body),
  updateUser: (id: number, body: T.UserWrite) => put<T.User>(`/iam/users/${id}`, body),
  changeUserStatus: (id: number, status: T.UserStatus) =>
    put<T.User>(`/iam/users/${id}/status`, { status }),
  assignRoles: (id: number, roleCodes: string[]) =>
    put<T.User>(`/iam/users/${id}/roles`, { roleCodes }),

  roles: () => get<T.Role[]>('/iam/roles'),
  role: (id: number) => get<T.Role>(`/iam/roles/${id}`),
  createRole: (body: T.RoleWrite) => post<T.Role>('/iam/roles', body),
  updateRole: (id: number, body: T.RoleWrite) => put<T.Role>(`/iam/roles/${id}`, body),
  deleteRole: (id: number) => del(`/iam/roles/${id}`),

  permissions: () => get<T.PermissionGroup[]>('/iam/permissions'),
}

export const vouchersApi = {
  list: () => get<T.Voucher[]>('/vouchers'),
  byId: (id: number) => get<T.Voucher>(`/vouchers/${id}`),
  create: (body: T.VoucherWrite) => post<T.Voucher>('/vouchers', body),
  update: (id: number, body: T.VoucherWrite) => put<T.Voucher>(`/vouchers/${id}`, body),
  setActive: (id: number, active: boolean) =>
    put<T.Voucher>(`/vouchers/${id}/activation`, { active }),

  codes: (id: number, params: { status?: T.VoucherCodeStatus; page?: number; size?: number }) =>
    get<T.Page<T.VoucherCode>>(`/vouchers/${id}/codes`, { params }),

  generateCodes: (id: number, body: T.VoucherCodeGenerateRequest) =>
    post<T.VoucherCode[]>(`/vouchers/${id}/codes`, body),

  byCode: (code: string) => get<T.VoucherCode>(`/vouchers/codes/${encodeURIComponent(code)}`),

  cancelCode: (code: string) =>
    post<T.VoucherCode>(`/vouchers/codes/${encodeURIComponent(code)}/cancel`),

  redeemCode: (code: string, body: T.VoucherRedeemRequest) =>
    post<T.VoucherRedemptionResult>(`/vouchers/codes/${encodeURIComponent(code)}/redeem`, body),

  analytics: () => get<T.VoucherAnalytics>('/vouchers/analytics'),
}

export const cashbackApi = {
  list: () => get<T.CashbackOffer[]>('/cashback-offers'),
  byId: (id: number) => get<T.CashbackOffer>(`/cashback-offers/${id}`),
  create: (body: T.CashbackOfferWrite) => post<T.CashbackOffer>('/cashback-offers', body),
  update: (id: number, body: T.CashbackOfferWrite) =>
    put<T.CashbackOffer>(`/cashback-offers/${id}`, body),
  setActive: (id: number, active: boolean) =>
    put<T.CashbackOffer>(`/cashback-offers/${id}/activation`, { active }),
}

export const campaignsApi = {
  preview: (filter: T.AudienceFilter, page = 0, size = 20) =>
    post<T.AudiencePage>('/campaigns/audience/preview', filter, { params: { page, size } }),

  exportAudience: (filter: T.AudienceFilter) =>
    download('/campaigns/audience/export', {
      method: 'POST',
      body: filter,
      fallbackName: 'campaign-audience.csv',
    }),

  sendSms: (body: { filter: T.AudienceFilter; template: string; campaignName?: string }) =>
    post<T.CampaignResult>('/campaigns/audience/sms', body),

  issueVouchers: (body: {
    filter: T.AudienceFilter; voucherId: number
    sendSms?: boolean; template?: string; campaignName?: string
  }) => post<T.CampaignResult>('/campaigns/audience/vouchers', body),
}

export const analyticsApi = {
  summary: () => get<T.AnalyticsSummary>('/analytics/summary'),
  pointsTrend: (days = 7) => get<T.DailyPoints[]>('/analytics/points-trend', { params: { days } }),
  segmentBreakdown: () => get<T.SegmentBreakdown[]>('/analytics/segments'),
}
